const API_URL = import.meta.env.VITE_PARSE_SERVER_URL;

let refreshPromise = null;

function getStoredUser() {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function setStoredUser(user) {
  localStorage.setItem('user', JSON.stringify(user));
}

export function getSessionToken() {
  const user = getStoredUser();
  return user?.sessionToken || null;
}

function getRefreshToken() {
  const user = getStoredUser();
  return user?.refreshToken || null;
}

async function attemptTokenRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await window.fetch(`${API_URL}/parse/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    const user = getStoredUser();
    if (user) {
      user.sessionToken = data.sessionToken;
      user.refreshToken = data.refreshToken;
      if (data.email !== undefined) user.email = data.email;
      if (data.username !== undefined) user.username = data.username;
      if (data.role !== undefined) user.role = data.role;
      if (data.avatar !== undefined) user.avatar = data.avatar;
      if (data.hasPassword !== undefined) user.hasPassword = data.hasPassword;
      setStoredUser(user);
    }
    return data.sessionToken;
  } catch {
    return null;
  }
}

export async function fetch(url, options = {}) {
  const fullUrl = `${API_URL}${url}`;

  const headers = {
    'Content-Type': 'application/json',
    'X-Parse-Application-Id': import.meta.env.VITE_PARSE_APP_ID,
    ...options.headers,
  };

  const token = getSessionToken();
  if (token) {
    headers['X-Parse-Session-Token'] = token;
  }

  const { signal, _isRetry, ...fetchOptions } = options;

  const response = await window.fetch(fullUrl, {
    ...fetchOptions,
    headers,
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({ error: response.statusText }));

    // On 401, try token refresh once before clearing the session
    if (response.status === 401 && !_isRetry) {
      if (!refreshPromise) {
        refreshPromise = attemptTokenRefresh();
      }
      const newToken = await refreshPromise;
      refreshPromise = null;

      if (newToken) {
        return fetch(url, { ...options, _isRetry: true, headers: { 'X-Parse-Session-Token': newToken } });
      }

      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth-change'));
    }

    throw new Error(errorBody.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function login(username, password, code, challengeToken, _ts) {
  return fetch('/parse/login', {
    method: 'POST',
    body: JSON.stringify({ username, password, code, challengeToken, _ts }),
  });
}

export async function googleLogin(credential) {
  return fetch('/parse/auth/google', {
    method: 'POST',
    body: JSON.stringify({ credential }),
  });
}

export async function register(username, password, email, code, challengeToken, _ts, newsletterSubscribed = false) {
  return fetch('/parse/users', {
    method: 'POST',
    body: JSON.stringify({ username, password, email, code, challengeToken, _ts, newsletterSubscribed }),
  });
}

export async function updateUser(id, data) {
  return fetch(`/parse/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAllUsers() {
  const data = await fetch('/parse/users');
  return data.results || [];
}

export async function getUserById(id) {
  return await fetch(`/parse/users/${id}`);
}

export async function adminUpdateUser(id, data) {
  return await fetch(`/parse/users/${id}/admin`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateUserRole(id, role) {
  return await fetch(`/parse/users/${id}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

export async function getMyProfile() {
  return await fetch('/parse/users/me');
}

// Email Template services
export async function getEmailTemplates() {
  const data = await fetch('/parse/classes/EmailTemplate');
  return data.results || [];
}

export async function getEmailTemplateById(id) {
  return await fetch(`/parse/classes/EmailTemplate/${id}`);
}

export async function updateEmailTemplate(id, templateData) {
  return await fetch(`/parse/classes/EmailTemplate/${id}`, {
    method: 'PUT',
    body: JSON.stringify(templateData),
  });
}

export async function sendTestEmail(to) {
  return await fetch('/parse/classes/EmailTemplate/send-test', {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
}

export async function resendVerification(email) {
  return fetch('/parse/login/resend-verification', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function getChallenge() {
  return fetch('/parse/functions/challenge');
}

export async function requestPasswordReset(email, code, challengeToken, _ts) {
  return fetch('/parse/request-password-reset', {
    method: 'POST',
    body: JSON.stringify({ email, code, challengeToken, _ts }),
  });
}

export async function validateResetToken(token) {
  return fetch(`/parse/validate-reset-token?token=${encodeURIComponent(token)}`);
}

export async function resetPassword(token, password) {
  return fetch('/parse/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

export async function getSiteSettings(options = {}) {
  try {
    const data = await fetch('/parse/classes/SiteSettings', options);
    const results = data.results || [];
    if (results.length === 0) return null;
    return { ...results[0] };
  } catch {
    return null;
  }
}

// Form services
export async function getForms() {
  const data = await fetch('/parse/classes/Form');
  return data.results || [];
}

export async function getForm(id) {
  return fetch(`/parse/classes/Form/${id}`);
}

export async function getFormBySlug(slug) {
  const data = await fetch(`/parse/classes/Form?where=${encodeURIComponent(JSON.stringify({ slug }))}`);
  const results = data.results || [];
  return results.length > 0 ? results[0] : null;
}

export async function submitFormEntry(data) {
  return fetch('/parse/classes/FormEntry', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFormEntries({ skip, limit } = {}) {
  const params = new URLSearchParams();
  if (skip != null) params.set('skip', String(skip));
  if (limit != null) params.set('limit', String(limit));
  const qs = params.toString();
  const data = await fetch(`/parse/classes/FormEntry${qs ? `?${qs}` : ''}`);
  return { results: data.results || [], count: data.count || 0 };
}

export async function getFormEntry(id) {
  return fetch(`/parse/classes/FormEntry/${id}`);
}

// Notification services
export async function getNotifications({ unreadOnly = false, skip = 0, limit = 10 } = {}) {
  const params = new URLSearchParams();
  if (unreadOnly) params.set('unread', 'true');
  if (skip) params.set('skip', String(skip));
  if (limit) params.set('limit', String(limit));
  const data = await fetch(`/api/admin/notifications?${params}`);
  return { results: data.results || [], count: data.count || 0 };
}

export async function markNotificationRead(id) {
  return fetch(`/api/admin/notifications/${id}`, {
    method: 'PUT',
  });
}

export async function markAllNotificationsRead() {
  return fetch('/api/admin/notifications/read-all', {
    method: 'PUT',
  });
}

export async function deleteNotification(id) {
  return fetch(`/api/admin/notifications/${id}`, {
    method: 'DELETE',
  });
}

export async function deleteFormEntry(id) {
  return fetch(`/parse/classes/FormEntry/${id}`, {
    method: 'DELETE',
  });
}

// Newsletter subscriber services
export async function subscribeToNewsletter(email, code, challengeToken) {
  return fetch('/parse/classes/NewsletterSubscriber/subscribe', {
    method: 'POST',
    body: JSON.stringify({ email, code, challengeToken }),
  });
}

export async function getSubscribers() {
  const data = await fetch('/parse/classes/NewsletterSubscriber');
  return data.results || [];
}

export async function deleteSubscriber(id) {
  return fetch(`/parse/classes/NewsletterSubscriber/${id}`, {
    method: 'DELETE',
  });
}

export async function updateSubscriberStatus(id, status) {
  return fetch(`/parse/classes/NewsletterSubscriber/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function resendSubscriberConfirmation(id) {
  return fetch(`/parse/classes/NewsletterSubscriber/${id}/resend-confirmation`, {
    method: 'POST',
  });
}

export async function updateUserPreferences(id, data) {
  return fetch(`/parse/users/${id}/preferences`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function checkMySubscription() {
  const data = await fetch('/parse/classes/NewsletterSubscriber/mine', { method: 'GET' });
  return data;
}

export async function getCampaigns() {
  const data = await fetch('/parse/classes/NewsletterCampaign');
  return data.results || [];
}

export async function getCampaignById(id) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}`);
}

export async function createCampaign(data) {
  return await fetch('/parse/classes/NewsletterCampaign', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCampaign(id, data) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCampaign(id) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}`, { method: 'DELETE' });
}

export async function sendCampaign(id) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}/send`, { method: 'POST' });
}

export async function sendTestCampaign(id) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}/send-test`, { method: 'POST' });
}

export async function cancelCampaign(id) {
  return await fetch(`/parse/classes/NewsletterCampaign/${id}/cancel`, { method: 'POST' });
}

export async function getNewsletterTemplates() {
  const data = await fetch('/parse/classes/NewsletterTemplate');
  return data.results || [];
}

export async function getNewsletterTemplateById(id) {
  return await fetch(`/parse/classes/NewsletterTemplate/${id}`);
}

export async function createNewsletterTemplate(data) {
  return await fetch('/parse/classes/NewsletterTemplate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNewsletterTemplate(id, data) {
  return await fetch(`/parse/classes/NewsletterTemplate/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNewsletterTemplate(id) {
  return await fetch(`/parse/classes/NewsletterTemplate/${id}`, { method: 'DELETE' });
}

export async function saveSiteSettings(data, objectId = null) {
  data.updatedAt = new Date();
  if (objectId) {
    return fetch(`/parse/classes/SiteSettings/${objectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  } else {
    return fetch('/parse/classes/SiteSettings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}
